import { Button, Card, Link } from "@heroui/react";
import { useAppSelector } from "@/hooks/useAppSelector.ts";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, ReactNode } from "react";
import { FaArrowRight, FaDoorClosed } from "react-icons/fa6";
import { toast } from "react-toastify";
import { api } from "@/api";
import { OfficeLocation } from "@/api/locations/locations.ts";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { Lock } from "@/app/components/lock/lock";
import { Ticket } from "@/api/tickets/tickets.ts";
import { ColumnType } from "@/app/components/tables/tableTypes";
import { OfficeLock } from "@/api/locks/locks";
import { setTour } from "@/app/store/user/reducer";
import { useAppDispatch } from "@/hooks/useAppDispatch";

function SuspenseMd({
  children,
  suspend,
}: {
  children: ReactNode;
  suspend: ReactNode;
}) {
  return (
    <>
      <div className="max-md:hidden">{children}</div>
      <div className="md:hidden">{suspend}</div>
    </>
  );
}

export function EmployeePage() {
  const user = useAppSelector((state) => state.user);
  const org = useAppSelector((state) => state.location.location);
  const [locations, setLocations] = useState<OfficeLocation[]>([]);
  const navigate = useNavigate();
  const [locks, setLocks] = useState<OfficeLock[]>([]);
  const [tickets, setTickets] = useState<Ticket[] | null>(null);

  const dispatch = useAppDispatch();
  const { tour } = useAppSelector((state) => state.user);

  useEffect(() => {
    if (tour.isTourActive && tour.run == false) {
      dispatch(setTour({ run: true, stepIndex: 0 }));
    }
  }, [tour, dispatch]);

  useEffect(() => {
    if (org.id == 0) return;

    api.locations
      .list()
      .then((data) => {
        setLocations(data.data);
      })
      .catch((err) => {
        console.log(err);
        toast.error("Произошла ошибка при загрузке локаций!");
      });

    api.tickets
      .list("user_to_landlord")
      .then((response) => {
        setTickets(response.data.filter((el) => el.status == "open"));
      })
      .catch((err) => {
        toast.error("Произошла ошибке при загрузке тикетов!");
        console.log(err);
      });

    api.locks
      .list()
      .then((response) => {
        setLocks(response.data);
      })
      .catch((err) => {
        toast.error("Произошла ошибка при загрузке замков!");
        console.log(err);
      });
  }, []);

  return (
    <div className="w-full h-full flex flex-col-reverse md:flex-row gap-4 p-2 md:p-4">
      <div className="flex flex-row flex-grow gap-2">
        <div className="flex flex-col flex-grow gap-2">
          <div className="hidden md:flex flex-row flex-wrap mb-2 gap-2 justify-between">
            <span className="greetings font-bold text-2xl">
              Доброго времени суток, {user.currentUser.first_name}!
            </span>
            <Button
              as={Link}
              href="/dashboard/meetingRooms"
              className="rent-meeting-room"
              color="primary"
            >
              Забронировать переговорную
            </Button>
          </div>

          <div className="flex flex-col mb-2 gap-2">
            <div className="flex flex-row gap-2">
              <div className="flex flex-col mb-2 mt-2 gap-4 flex-grow">
                <SuspenseMd
                  suspend={
                    <Button
                      className="w-full"
                      as={Link}
                      href="/dashboard/locations"
                      variant="solid"
                      color="primary"
                    >
                      Просмотреть локации
                    </Button>
                  }
                >
                  <div className="flex flex-row gap-2 items-center mb-2">
                    <span className="font-semibold text-xl">Локации</span>
                  </div>
                  <TableBuilder
                    displayOptions={false}
                    displaySearch={false}
                    columns={[
                      {
                        label: "Название",
                        key: "display_name",
                        type: ColumnType.String,
                      },
                      {
                        label: "Адрес",
                        key: "address",
                        type: ColumnType.String,
                      },
                      {
                        label: "Город",
                        key: "city",
                        type: ColumnType.String,
                      },
                      {
                        label: "Станция метро",
                        key: "metro_station",
                        type: ColumnType.String,
                      },
                      {
                        label: "Район",
                        key: "district",
                        type: ColumnType.String,
                      },
                      {
                        key: "actions",
                        label: "Действия",
                        type: ColumnType.Actions,
                        actions: [
                          {
                            icon: <FaDoorClosed />,
                            onClick: (_value, row) => {
                              navigate(
                                `/dashboard/locations/${row.id}/offices`,
                              );
                            },
                          },
                        ],
                      },
                    ]}
                    data={locations}
                  />
                </SuspenseMd>
                <SuspenseMd
                  suspend={
                    <Button
                      className="w-full"
                      as={Link}
                      href="/dashboard/tickets"
                      variant="solid"
                      color="primary"
                    >
                      Просмотреть обращения
                    </Button>
                  }
                >
                  <div className="flex flex-col mt-2 mb-2 gap-2">
                    <div className="flex flex-row gap-2 items-center">
                      <span className="font-semibold text-xl">Обращения</span>
                    </div>
                    <TableBuilder
                      displayOptions={false}
                      displaySearch={false}
                      columns={[
                        {
                          key: "subject",
                          label: "Тема обращения",
                          type: ColumnType.String,
                        },
                        {
                          key: "category",
                          label: "Категория",
                          type: ColumnType.Category,
                          sortable: true,
                        },
                        {
                          key: "office_id",
                          label: "Офис",
                          type: ColumnType.Office,
                        },
                        {
                          key: "created_at",
                          label: "Создан",
                          type: ColumnType.DateTime,
                          sortable: true,
                        },
                        {
                          key: "actions",
                          label: "Действия",
                          type: ColumnType.Actions,
                          actions: [
                            {
                              icon: <FaArrowRight />,
                              onClick: (_value, row) => {
                                navigate(
                                  `/dashboard/tickets/${row.type}/${row.id}`,
                                );
                              },
                            },
                          ],
                        },
                      ]}
                      data={tickets || []}
                    />
                  </div>
                </SuspenseMd>
              </div>

              {locks.length > 0 ? (
                <div className="locks max-h-fit max-md:hidden md:p-2 p-4">
                  <div className="flex flex-row flex-wrap gap-2">
                    {locks.map((lock) => (
                      <Lock
                        key={lock.id}
                        lock={{ id: lock.id, displayName: lock.display_name }}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-col md:hidden mb-2 gap-2">
          <span className="greetings font-bold text-2xl">
            Доброго времени суток, {user.currentUser.first_name}!
          </span>
          <Button
            as={Link}
            href="/dashboard/meetingRooms"
            className="rent-meeting-room w-full mt-2"
            color="primary"
          >
            Забронировать переговорную
          </Button>
        </div>
        {locks.length > 0 ? (
          <Card className="locks max-h-fit md:p-2 p-4 md:hidden">
            <div className="flex flex-row flex-wrap gap-2">
              {locks.map((lock) => (
                <Lock
                  key={lock.id}
                  lock={{ id: lock.id, displayName: lock.display_name }}
                />
              ))}
            </div>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
